import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Loader, RotateCw } from "lucide-react";

export default function Flashcards() {
  const { examType, discipline } = useParams();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newCard, setNewCard] = useState({ front: "", back: "", topic: "" });

  useEffect(() => {
    fetchCards();
  }, [examType, discipline]);

  const fetchCards = async () => {
    try {
      const res = await api.get("/flashcards", {
        params: { exam_type: examType, discipline }
      });
      setCards(res.data);
    } catch (err) {
      console.error("Error fetching flashcards:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newCard.front || !newCard.back) {
      toast.error("Please fill in both sides");
      return;
    }

    try {
      const res = await api.post("/flashcards", {
        exam_type: examType,
        discipline,
        topic: newCard.topic || "General",
        front: newCard.front,
        back: newCard.back
      });
      setCards(prev => [...prev, res.data]);
      setNewCard({ front: "", back: "", topic: "" });
      setShowAdd(false);
      toast.success("Flashcard added!");
    } catch (err) {
      toast.error("Failed to add flashcard");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/flashcards/${id}`);
      setCards(prev => prev.filter(c => c.flashcard_id !== id));
      if (currentIndex >= cards.length - 1) {
        setCurrentIndex(Math.max(0, cards.length - 2));
      }
      toast.success("Flashcard deleted");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const nextCard = () => {
    setFlipped(false);
    setTimeout(() => {
      setCurrentIndex(prev => (prev + 1) % cards.length);
    }, 100);
  };

  const prevCard = () => {
    setFlipped(false);
    setTimeout(() => {
      setCurrentIndex(prev => (prev - 1 + cards.length) % cards.length);
    }, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const card = cards[currentIndex];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="btn-ghost p-2">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Flashcards</h1>
              <p className="text-sm text-slate-500">{discipline.replace(/_/g, " ")}</p>
            </div>
          </div>
          <button
            data-testid="add-card-btn"
            onClick={() => setShowAdd(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={18} />
            Add Card
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {cards.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-600 mb-4">No flashcards yet</p>
            <button onClick={() => setShowAdd(true)} className="btn-primary">
              Create Your First Card
            </button>
          </div>
        ) : (
          <>
            {/* Card counter */}
            <p className="text-center text-slate-500 mb-4">
              Card {currentIndex + 1} of {cards.length}
            </p>

            {/* Flashcard */}
            <div
              className={`flashcard cursor-pointer mb-6 ${flipped ? "flipped" : ""}`}
              onClick={() => setFlipped(!flipped)}
            >
              <div className="flashcard-inner relative h-64">
                {/* Front */}
                <div className="flashcard-front absolute inset-0 card flex items-center justify-center p-8 bg-white">
                  <div className="text-center">
                    <span className="text-xs text-blue-600 font-medium mb-2 block">{card.topic}</span>
                    <p className="text-xl font-medium text-slate-900">{card.front}</p>
                    <p className="text-sm text-slate-400 mt-4">Click to flip</p>
                  </div>
                </div>
                {/* Back */}
                <div className="flashcard-back absolute inset-0 card flex items-center justify-center p-8 bg-blue-50">
                  <div className="text-center">
                    <p className="text-xl font-medium text-slate-900">{card.back}</p>
                    <p className="text-sm text-slate-400 mt-4">Click to flip</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <button onClick={prevCard} className="btn-secondary">
                Previous
              </button>
              <button
                onClick={() => handleDelete(card.flashcard_id)}
                className="text-red-500 hover:text-red-700 p-2"
              >
                <Trash2 size={20} />
              </button>
              <button onClick={nextCard} className="btn-primary">
                Next
              </button>
            </div>
          </>
        )}

        {/* Add modal */}
        {showAdd && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="card max-w-md w-full">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Add Flashcard</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Topic</label>
                  <input
                    type="text"
                    value={newCard.topic}
                    onChange={e => setNewCard(prev => ({ ...prev, topic: e.target.value }))}
                    placeholder="e.g., Thermodynamics"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Front (Question)</label>
                  <textarea
                    value={newCard.front}
                    onChange={e => setNewCard(prev => ({ ...prev, front: e.target.value }))}
                    placeholder="What is the first law of thermodynamics?"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Back (Answer)</label>
                  <textarea
                    value={newCard.back}
                    onChange={e => setNewCard(prev => ({ ...prev, back: e.target.value }))}
                    placeholder="Energy cannot be created or destroyed..."
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowAdd(false)} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button onClick={handleAdd} className="btn-primary flex-1">
                  Add Card
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
